import React from 'react'
import { useIntl } from 'react-intl'
import {
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  MenuItem,
  MenuList,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import { useKeycloak } from '@react-keycloak/web'
import SubscriptionServices from '../../../../../Services/Consumers/SubscriptionServices'
import { useStyles } from './Styles'

const UserGroupAutocomplete = ({ selectedUserGroups = [], onUserGroupAdd, onUserGroupRemove, handleNotificationCall }) => {
  const intl = useIntl()
  const classes = useStyles()
  const { keycloak } = useKeycloak()

  const [groupSearchText, setGroupSearchText] = React.useState('')
  const [isGroupSearching, setIsGroupSearching] = React.useState(false)
  const [groupSearchResults, setGroupSearchResults] = React.useState([])
  const [showGroupDropdown, setShowGroupDropdown] = React.useState(false)
  const debounceTimer = React.useRef(null)

  // Cleanup debounce timer on unmount
  React.useEffect(() => {
    const timer = debounceTimer.current

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [])

  // User group search handler
  const handleGroupSearch = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setGroupSearchResults([])
      setShowGroupDropdown(false)
      return
    }
    setIsGroupSearching(true)
    SubscriptionServices.getUserGroups({
      headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
      params: { name: searchTerm }
    }).then((result) => {
      if (!!result && !!result?.data && result?.data?.status?.code === 200) {
        // Filter out already selected groups
        const selectedIds = selectedUserGroups.map(g => g.id)
        const filteredResults = result.data.data.filter(group => !selectedIds.includes(group.id))
        setGroupSearchResults(filteredResults)
        setShowGroupDropdown(true)
      } else {
        let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
        if (result.response && result.response.data) {
          if (result.response.data && result.response.data.status && result.response.data.status.errors) {
            message = result.response.data.status.errors
          }
        }
        handleNotificationCall({
          type: 'defence',
          message: message,
          color: 'error'
        }, 'error')
        setGroupSearchResults([])
        setShowGroupDropdown(false)
      }
    }).then(() => {
      setIsGroupSearching(false)
    })
  }

  // Debounced search change handler
  const handleGroupSearchChange = (e) => {
    const value = e.target.value
    setGroupSearchText(value)
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }
    debounceTimer.current = setTimeout(() => {
      handleGroupSearch(value)
    }, 500)
  }

  // Handle user group selection
  const handleSelectGroup = (group) => {
    onUserGroupAdd(group)
    setGroupSearchText('')
    setGroupSearchResults([])
    setShowGroupDropdown(false)
  }

  // Handle user group removal
  const handleRemoveGroup = (groupId) => {
    onUserGroupRemove(groupId)
  }

  return (
    <FormControl variant="outlined">
      <ClickAwayListener onClickAway={() => setShowGroupDropdown(false)}>
        <div className={classes.autocompleteWrapper}>
          <TextField
            placeholder={intl.formatMessage({ id: 'Fields.input.PromocodeRuleUserGroups.placeholder' })}
            id="user_group_ids"
            type={'text'}
            margin="dense"
            variant="outlined"
            value={groupSearchText}
            onChange={handleGroupSearchChange}
            onFocus={() => {
              if (groupSearchResults.length > 0) {
                setShowGroupDropdown(true)
              }
            }}
            InputProps={{
              endAdornment: isGroupSearching && <CircularProgress size={20} />
            }}
          />

          {/* Dropdown Results */}
          {showGroupDropdown && (
            <Paper className={classes.dropdown}>
              {
                groupSearchResults.length > 0 ?
                  <MenuList>
                    {groupSearchResults.map((group) => (
                      <MenuItem
                        key={group.id}
                        onClick={() => handleSelectGroup(group)}
                        className={classes.menuItem}
                      >
                        <div>
                          <div className={classes.userName}>
                            {group.name}
                          </div>
                        </div>
                      </MenuItem>
                    ))}
                  </MenuList>
                  :
                  <Typography variant="body1" className={classes.usersNoResultsText}>
                    {intl.formatMessage({ id: 'UserGroups.List.NoResults' })}
                  </Typography>
              }
            </Paper>
          )}

          {/* Selected User Groups */}
          {selectedUserGroups.length > 0 && (
            <Box className={classes.selectedUsersBox}>
              {selectedUserGroups.map((group) => (
                <Chip
                  key={group.id}
                  label={group.name}
                  onDelete={() => handleRemoveGroup(group.id)}
                  className={classes.userChip}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </div>
      </ClickAwayListener>
    </FormControl>
  )
}

export default UserGroupAutocomplete

